import ConfigView              from 'scripts/defa/basic/view-config-view';
import UnitTestCollectionView  from 'scripts/defa/unit-tests/view-unit-test-collection-view';
import UnitTestFeedbackView    from 'scripts/defa/unit-tests/view-unit-test-feedback-view';
import UnitTestFirmwareView    from 'scripts/defa/unit-tests/view-unit-test-firmware-view';
import UnitTestNoInstallerForm from 'scripts/defa/unit-tests/view-unit-test-no-installer-form';
import UserContainer           from 'scripts/defa/users/view-user-container';
/**
 * defa.views.UnitTestFlow
 * A flow for both functionality and firmware unit tests.
 * @version 1.2.0
 */
import Flow                    from 'scripts/poppet/flow';
import template                from 'templates/unitTestFlow.jst.html';

const selectors = {
	firmware     : '[data-region=unit-test-firmware]',
	functionality: '[data-region=unit-test-functionality]',
	feedback     : '[data-region=unit-test-feedback]',
};

const channelUser   = Bb.Radio.channel( 'user' );
const channelLayout = Bb.Radio.channel( 'layout' );

const UnitTestFlow = Flow.extend( {
	
	// ------------------------------------------------
	// Setup
	// ------------------------------------------------
	
	template  : template,
	mainRegion: 'feedback',
	
	modelEvents: {
		'error': 'onModelError',
	},
	
	regions: {
		firmware     : selectors.firmware,
		functionality: selectors.functionality,
		feedback     : selectors.feedback,
	},
	
	// ------------------------------------------------
	// Event Handlers
	// ------------------------------------------------
	
	/**
	 * @constructor
	 */
	initialize: function ( options ) {
		
		Flow.prototype.initialize.call( this, options );
		
		this.model.set( 'transientTesting', true );
		this.model.set( 'tested', false );
		
	},
	
	/**
	 * Triggers if the model experiences an error.
	 * @void
	 */
	onModelError: function () {
		channelLayout.trigger( 'error', this.model );
	},
	
	// ------------------------------------------------
	// Other functions
	// ------------------------------------------------
	
	// dispatches
	
	/**
	 * Selects which view should be shown next
	 * @void
	 */
	dispatch: function () {
		
		let self         = this,
			fn           = false,
			firmware     = self.model.get( 'transientFirmware' ),
			tested       = self.model.get( 'tested' ),
			unitFeedback = self.unitFeedback || false;
		
		// feedback is always false upon a new fetch.
		// it's only true if the unit is set in the front end.
		
		if ( !firmware ) {
			fn = self.showFirmwareUpgrader;
		}
		
		if ( firmware && !unitFeedback && !tested ) {
			fn = self.showFunctionalityTests;
		}
		
		if ( firmware && unitFeedback && !tested ) {
			fn = self.showFeedback;
		}
		
		if ( firmware && unitFeedback && tested ) {
			fn = function () {
				self.trigger( 'unit:tested', this.model.get( 'id' ) );
			};
		}
		
		if ( _.isFunction( fn ) ) {
			fn.call( self );
		}
		
	},
	
	/**
	 * Dispatches the user to become logged in.
	 * @void
	 */
	verificationDispatch: function () {
		
		let user          = channelUser.request( 'model' ),
			loggedIn      = channelUser.request( 'is:logged:in' ),
			installerRole = 'INSTALLER_' + this.model.get( 'family' ),
			installer     = user.hasRoles( [ installerRole, ] );
		
		if ( !loggedIn ) {
			
			let userContainer = new UserContainer( {
				view: 'user:login',
				user: user,
			} );
			
			this.listenTo( user, 'sync', function () {
				this.verificationDispatch();
			} );
			
			this.showChildView( 'feedback', userContainer );
		}
		
		if ( loggedIn && !installer ) {
			
			this.model.set( 'transientTesting', false );
			
			let unitTestNoInstallerForm = new UnitTestNoInstallerForm( {
				closeLink: {
					event: 'unit:list',
				},
			} );
			
			this.showChildView( 'feedback', unitTestNoInstallerForm );
		}
		
		if ( loggedIn && installer ) {
			this.approveTest();
		}
		
	},
	
	// views
	
	/**
	 * Checks the status of the firmware and updates as necessary.
	 * @void
	 */
	showFirmwareUpgrader: function () {
		// create the firmware view.
		let unitTestFirmwareView = new UnitTestFirmwareView( {
			model: this.model,
		} );
		
		// listen for the model to update the firmware or fail.
		this.listenToOnce( this.model, 'firmware:upgraded', function () {
			this.dispatch();
		} );
		
		this.listenToOnce( this.model, 'firmware:error', function () {
			unitTestFirmwareView.failed();
			this.unitFeedback = 'error';
			this.showFeedback();
		} );
		
		// empty other regions, attach the view and start the test.
		this.showChildView( 'firmware', unitTestFirmwareView );
		this.model.updateFirmware();
		
	},
	
	/**
	 * Polls and views functionality tests for the unit.
	 * @void
	 */
	showFunctionalityTests: function () {
		// add a waiting view to the region first.
		let unitTestConfigView = new ConfigView( {
			context: 'test',
		} );
		this.showChildView( 'functionality', unitTestConfigView );
		
		// listen for tests complete
		this.listenToOnce( this.model, 'tests:complete', function ( tests, feedback ) {
			// save the tests
			this.tests = tests;
			
			// create a view of the tests
			let unitTestCollectionView = new UnitTestCollectionView( {
				collection: tests,
			} );
			
			// listen for the view to complete animating before running dispatch again
			this.listenToOnce( unitTestCollectionView, 'feedback:ready', function () {
				this.unitFeedback = feedback;
				this.dispatch();
			} );
			
			// attach the tests view
			this.showChildView( 'functionality', unitTestCollectionView );
			
		} );
		
		this.listenToOnce( this.model, 'tests:failed', function ( error ) {
			unitTestConfigView.showErrorMessage( error );
			this.showFeedback();
		} );
		
		// empty other regions and start the test
		this.getRegion( 'feedback' ).empty();
		this.model.testFunctionality();
		
	},
	
	/**
	 * Shows a feedback frame, with options depending on the test results
	 * @void
	 */
	showFeedback: function () {
		
		let hasWarmUp = false;
		this.tests.each( function ( test ) {
			if ( 'WarmUp' === test.get( 'title' ) && test.get( 'tests' ).length !== 0 ) {
				hasWarmUp = true;
			}
		} );
		
		let unitTestFeedbackView = new UnitTestFeedbackView( {
			model    : this.model,
			feedback : this.unitFeedback,
			hasWarmUp: hasWarmUp,
		} );
		
		this.listenTo( unitTestFeedbackView, 'retest', function () {
			this.unitFeedback = false;
			this.dispatch();
		} );
		
		this.listenTo( unitTestFeedbackView, 'verify', function () {
			this.verificationDispatch();
		} );
		
		this.listenTo( unitTestFeedbackView, 'close', function () {
			this.approveTest( unitTestFeedbackView );
		} );
		
		this.showChildView( 'feedback', unitTestFeedbackView );
	},
	
	// completion
	
	/**
	 * Saves the test results and dispatches.
	 * @void
	 */
	approveTest: function ( view ) {
		
		this.listenToOnce( this.model, 'sync', function () {
			this.dispatch();
		} );
		
		this.listenToOnce( this.model, 'error', function ( /* model, response , options */ ) {
			view.idle();
		} );
		
		this.model.saveTest();
	},
	
} );

export default UnitTestFlow;